import { Badge, Button, Group, Text } from '@mantine/core';
import { IconFileText, IconLayoutColumns } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PresenceAvatars } from '@/components/presence/PresenceAvatars';
import type { UseResourcePresenceReturn } from '@/hooks/useResourcePresence';
import { presenceRowKey } from '@/lib/presence/presenceFhir';
import { DateRangeSelect } from '@/components/DateRangeSelect';
import {
  UnifyTable,
  type UnifyTableAdvancedFilterSection,
  type UnifyTableColumn,
  type UnifyTableMultiFilterConfig,
} from '@/components/UnifyTable';
import type { CalendarDateRange } from '@/lib/dateHelpers';
import { ColumnSelectModal } from '@/pages/episode-tracker/components/ColumnSelectModal';
import { formatCurrency, formatDate, formatDateTime, titleCase } from './components/billingFormatters';
import { RCM_STATUS_COLORS, rcmStatusLabel } from './components/rcmStatus';
import {
  MOCK_CLAIMS,
  claimLineItemCount,
  claimServiceDateRange,
  claimTotal,
  type ClaimRow,
  type ClaimStatus,
} from './mockClaims';

const CLAIMS_SCOPE = 'billing-claims' as const;

const HIDDEN_BY_DEFAULT = new Set(['provider', 'use']);

const STATUS_COLORS: Record<ClaimStatus, string> = {
  active: 'green',
  draft: 'gray',
  cancelled: 'red',
  'entered-in-error': 'orange',
};

function formatServiceDate(claim: ClaimRow): string {
  const { start, end } = claimServiceDateRange(claim);
  if (!start) return '—';
  return start === end ? formatDate(start) : `${formatDate(start)} – ${formatDate(end)}`;
}

function optionsFor(rows: ClaimRow[], select: (row: ClaimRow) => string): string[] {
  return [...new Set(rows.map(select).filter(Boolean))].sort((a, b) => a.localeCompare(b));
}

function claimInDateRange(claim: ClaimRow, [from, to]: CalendarDateRange): boolean {
  if (!from && !to) return true;
  const { start, end } = claimServiceDateRange(claim);
  if (!start) return false;
  const spanStart = dayjs(start).startOf('day');
  const spanEnd = dayjs(end || start).endOf('day');
  if (from && spanEnd.isBefore(dayjs(from).startOf('day'))) return false;
  if (to && spanStart.isAfter(dayjs(to).endOf('day'))) return false;
  return true;
}

export interface ClaimsTableProps {
  presenceByRow?: UseResourcePresenceReturn['presenceByRow'];
  setActiveRow?: UseResourcePresenceReturn['setActiveRow'];
}

export function ClaimsTable({ presenceByRow, setActiveRow }: ClaimsTableProps): JSX.Element {
  const navigate = useNavigate();
  const [activeClaimId, setActiveClaimId] = useState<string | null>(null);
  const [columnModalOpen, setColumnModalOpen] = useState(false);
  const [visibleColumnOrder, setVisibleColumnOrder] = useState<string[] | null>(null);
  const [dateRange, setDateRange] = useState<CalendarDateRange>([null, null]);

  const data = useMemo(
    () => MOCK_CLAIMS.filter((claim) => claimInDateRange(claim, dateRange)),
    [dateRange],
  );

  useEffect(() => {
    setActiveRow?.(
      activeClaimId
        ? {
            rowId: activeClaimId,
            scope: CLAIMS_SCOPE,
            subjectResourceType: 'Claim',
          }
        : null,
    );
  }, [activeClaimId, setActiveRow]);

  const columns = useMemo(
    (): UnifyTableColumn<ClaimRow>[] => [
      {
        key: 'identifier',
        label: 'Claim ID',
        render: (row) => <Text size="xs" c="dimmed">{row.identifier}</Text>,
        getValue: (row) => row.identifier,
      },
      {
        key: 'created',
        label: 'Created',
        render: (row) => <Text size="sm">{formatDate(row.created)}</Text>,
        getValue: (row) => row.created,
      },
      {
        key: 'lastUpdatedOn',
        label: 'Last Updated On',
        render: (row) => <Text size="sm">{formatDateTime(row.lastUpdatedOn)}</Text>,
        getValue: (row) => row.lastUpdatedOn,
      },
      {
        key: 'lastUpdatedBy',
        label: 'Last Updated By',
        render: (row) => <Text size="sm">{row.lastUpdatedBy}</Text>,
        getValue: (row) => row.lastUpdatedBy,
      },
      {
        key: 'serviceDate',
        label: 'Date of Service',
        render: (row) => <Text size="sm">{formatServiceDate(row)}</Text>,
        getValue: (row) => claimServiceDateRange(row).start,
      },
      {
        key: 'rcmStatus',
        label: 'RCM Status',
        render: (row) => (
          <Badge variant="light" size="sm" color={RCM_STATUS_COLORS[row.rcmStatus]}>
            {rcmStatusLabel(row.rcmStatus)}
          </Badge>
        ),
        getValue: (row) => rcmStatusLabel(row.rcmStatus),
      },
      {
        key: 'status',
        label: 'Claim Status',
        render: (row) => (
          <Badge variant="light" size="sm" color={STATUS_COLORS[row.status]}>
            {titleCase(row.status)}
          </Badge>
        ),
        getValue: (row) => titleCase(row.status),
      },
      {
        key: 'patientName',
        label: 'Client',
        render: (row) => <Text size="sm" fw={600}>{row.patientName}</Text>,
        getValue: (row) => row.patientName,
      },
      {
        key: 'payer',
        label: 'Payer',
        getValue: (row) => row.payer,
      },
      {
        key: 'type',
        label: 'Type',
        getValue: (row) => row.type,
      },
      {
        key: 'use',
        label: 'Use',
        render: (row) => <Text size="sm">{titleCase(row.use)}</Text>,
        getValue: (row) => titleCase(row.use),
      },
      {
        key: 'totalAmount',
        label: 'Total',
        align: 'right',
        render: (row) => <Text size="sm">{formatCurrency(claimTotal(row))}</Text>,
        getValue: (row) => String(Math.round(claimTotal(row))).padStart(8, '0'),
      },
      {
        key: 'encounterCount',
        label: 'Encounters',
        align: 'right',
        render: (row) => <Text size="sm">{row.encounters.length}</Text>,
        getValue: (row) => String(row.encounters.length).padStart(4, '0'),
      },
      {
        key: 'itemCount',
        label: 'Line Items',
        align: 'right',
        render: (row) => <Text size="sm">{claimLineItemCount(row)}</Text>,
        getValue: (row) => String(claimLineItemCount(row)).padStart(4, '0'),
      },
      {
        key: 'provider',
        label: 'Provider',
        getValue: (row) => row.provider,
      },
      {
        key: 'actions',
        label: 'Actions',
        sortable: false,
        align: 'right',
        render: (row) => (
          <Group gap="xs" justify="flex-end" wrap="nowrap" onClick={(e) => e.stopPropagation()}>
            <PresenceAvatars
              actors={presenceByRow?.get(presenceRowKey(CLAIMS_SCOPE, row.id)) ?? []}
            />
            <Button
              component={Link}
              to={`/dashboard/billing/claims/${encodeURIComponent(row.id)}/cms1500`}
              target="_blank"
              rel="noopener noreferrer"
              size="xs"
              variant="light"
              leftSection={<IconFileText size={14} />}
            >
              CMS-1500
            </Button>
          </Group>
        ),
        getValue: () => '',
      },
    ],
    [presenceByRow],
  );

  const columnOptions = useMemo(
    () =>
      columns
        .filter((c) => c.key !== 'actions')
        .map((c) => ({ key: c.key, displayLabel: c.label })),
    [columns],
  );

  const defaultColumnKeys = useMemo(
    () =>
      columns.map((c) => c.key).filter((key) => key !== 'actions' && !HIDDEN_BY_DEFAULT.has(key)),
    [columns],
  );

  const visibleColumnKeys = useMemo(
    () => [...(visibleColumnOrder ?? defaultColumnKeys), 'actions'],
    [visibleColumnOrder, defaultColumnKeys],
  );

  const multiFilters = useMemo(
    (): UnifyTableMultiFilterConfig[] => [
      {
        columnKey: 'rcmStatus',
        allLabel: 'All RCM statuses',
        placeholder: 'RCM Status',
        data: ['Ready', 'Submitted', 'Acknowledged', 'Adjudicated', 'Denied', 'Rejected'],
        emptyMeansAll: true,
      },
      {
        columnKey: 'status',
        allLabel: 'All statuses',
        placeholder: 'Claim Status',
        data: ['Active', 'Draft', 'Cancelled', 'Entered In Error'],
        emptyMeansAll: true,
      },
      {
        columnKey: 'payer',
        allLabel: 'All payers',
        placeholder: 'Payer',
        data: optionsFor(MOCK_CLAIMS, (r) => r.payer),
        emptyMeansAll: true,
      },
    ],
    [],
  );

  const advancedFilterSections = useMemo(
    (): UnifyTableAdvancedFilterSection[] => [
      {
        label: 'Use',
        columnKey: 'use',
        options: [
          { value: 'Claim', label: 'Claim' },
          { value: 'Preauthorization', label: 'Preauthorization' },
          { value: 'Predetermination', label: 'Predetermination' },
        ],
      },
    ],
    [],
  );

  return (
    <>
      <UnifyTable
        title="Claims"
        subtitle={`${data.length} claims`}
        columns={columns}
        visibleColumnKeys={visibleColumnKeys}
        data={data}
        getRowKey={(row) => row.id}
        searchable
        searchPlaceholder="Search by client, provider, payer, claim ID…"
        sortable
        pagination
        defaultRowsPerPage={10}
        multiFilters={multiFilters}
        advancedFilterSections={advancedFilterSections}
        advancedFiltersInHeader
        extraFilters={
          <DateRangeSelect
            value={dateRange}
            onChange={setDateRange}
            placeholder="Date of service"
            size="sm"
            w={260}
          />
        }
        onRowClick={(row) => {
          setActiveClaimId(row.id);
          navigate(`/dashboard/billing/claims/${encodeURIComponent(row.id)}`);
        }}
        headerRight={
          <Button
            variant="outline"
            leftSection={<IconLayoutColumns size={16} />}
            onClick={() => setColumnModalOpen(true)}
          >
            Columns
          </Button>
        }
      />

      <ColumnSelectModal
        opened={columnModalOpen}
        onClose={() => setColumnModalOpen(false)}
        allColumns={columnOptions}
        defaultColumnKeys={defaultColumnKeys}
        value={visibleColumnOrder}
        onApply={setVisibleColumnOrder}
      />
    </>
  );
}
